import { TFunction } from 'i18next';
import { KpiType } from '../../../../../constants';
import {
  KpiTargetsProps,
  SCOHourlyTurnoverPerformanceItem,
  SalesNetPerformanceItem,
  SalesNetResponse,
} from '../../../../../hooks';
import { Typographies } from '../../../../../theme';
import { homeTranslations } from '../../../../../translations';
import { getKpiTargetValue, Translation } from '../../../../../utils';

export type SalesNetPerformanceItemKey =
  | 'netSales'
  | 'netSalesSCO'
  | 'netSalesRFIDSCO'
  | 'netSalesMobile'
  | 'regularNetSales';

export type SalesNetTabId = 'all' | 'regular' | 'sco' | 'rfidSco' | 'mobile';

export type SalesNetTabForecastApi = 'netSale' | 'netSaleSCO' | null;

export type SalesNetComparisonValues = {
  today: number;
  lastWeek: number;
  lastYear: number;
};

export type SalesNetTabConfig = {
  id: SalesNetTabId;
  labelTranslation: Translation;
  metricKey: SalesNetPerformanceItemKey;
  supportsTarget: boolean;
  supportsForecast: boolean;
  forecastApiName: SalesNetTabForecastApi;
  targetKpiType: KpiType | null;
};

export type SalesNetComparisonMap = Record<SalesNetTabId, SalesNetComparisonValues>;

export type SalesNetTargetModel = {
  targetKpiType: KpiType | null;
  targetValue: number | null;
  showTarget: boolean;
};

export type SalesNetForecastModel = {
  shouldRequestForecast: boolean;
  forecastApiName: SalesNetTabForecastApi;
};

export const salesNetTabs: SalesNetTabConfig[] = [
  {
    id: 'all',
    labelTranslation: homeTranslations.common.allTabName,
    metricKey: 'netSales',
    supportsTarget: true,
    supportsForecast: true,
    forecastApiName: 'netSale',
    targetKpiType: 'dailyTurnOver',
  },
  {
    id: 'regular',
    labelTranslation: homeTranslations.hourlyTurnover.tabs.regularSales,
    metricKey: 'regularNetSales',
    supportsTarget: false,
    supportsForecast: false,
    forecastApiName: null,
    targetKpiType: null,
  },
  {
    id: 'sco',
    labelTranslation: homeTranslations.hourlyTurnover.tabs.scoSales,
    metricKey: 'netSalesSCO',
    supportsTarget: true,
    supportsForecast: true,
    forecastApiName: 'netSaleSCO',
    targetKpiType: 'dailySCOTurnOver',
  },
  {
    id: 'rfidSco',
    labelTranslation: homeTranslations.hourlyTurnover.tabs.rfidScoSales,
    metricKey: 'netSalesRFIDSCO',
    supportsTarget: false,
    supportsForecast: false,
    forecastApiName: null,
    targetKpiType: null,
  },
  {
    id: 'mobile',
    labelTranslation: homeTranslations.hourlyTurnover.tabs.mobileSales,
    metricKey: 'netSalesMobile',
    supportsTarget: false,
    supportsForecast: false,
    forecastApiName: null,
    targetKpiType: null,
  },
];

export const getSalesNetTabByIndex = (tabIndex: number) => {
  return salesNetTabs[tabIndex] ?? salesNetTabs[0];
};

export const createInitialComparisonMap = (): SalesNetComparisonMap => {
  return {
    all: { today: 0, lastWeek: 0, lastYear: 0 },
    regular: { today: 0, lastWeek: 0, lastYear: 0 },
    sco: { today: 0, lastWeek: 0, lastYear: 0 },
    rfidSco: { today: 0, lastWeek: 0, lastYear: 0 },
    mobile: { today: 0, lastWeek: 0, lastYear: 0 },
  };
};

export const mapSalesNetResponseToComparisonMap = (response: SalesNetResponse): SalesNetComparisonMap => {
  return {
    all: {
      today: response.today,
      lastWeek: response.lastWeek,
      lastYear: response.lastYear,
    },
    regular: {
      today: response.todayRegular,
      lastWeek: response.lastWeekRegular,
      lastYear: response.lastYearRegular,
    },
    sco: {
      today: response.todaySCO,
      lastWeek: response.lastWeekSCO,
      lastYear: response.lastYearSCO,
    },
    rfidSco: {
      today: response.todayRFIDSCO,
      lastWeek: response.lastWeekRFIDSCO,
      lastYear: response.lastYearRFIDSCO,
    },
    mobile: {
      today: response.todayMobile,
      lastWeek: response.lastWeekMobile,
      lastYear: response.lastYearMobile,
    },
  };
};

export const getTotalNetSales = (
  items: (SalesNetPerformanceItem | null)[],
  metric: SalesNetPerformanceItemKey,
) => {
  return items.reduce((acc, val) => acc + (val ? val[metric] : 0), 0);
};

export const getSalesNetHasValue = (item: SalesNetPerformanceItem, metric: SalesNetPerformanceItemKey) => {
  return Math.floor(item[metric]) !== 0;
};

export const showTargetTile = (tab: SalesNetTabConfig) => {
  return tab.supportsTarget;
};

export const showForecastTile = (tab: SalesNetTabConfig) => {
  return tab.supportsForecast;
};

export const buildTargetModel = (
  tab: SalesNetTabConfig,
  kpiTargetsProps: KpiTargetsProps | null,
): SalesNetTargetModel => {
  const targetValue =
    tab.targetKpiType && kpiTargetsProps ? getKpiTargetValue(kpiTargetsProps, tab.targetKpiType) : null;

  return {
    targetKpiType: tab.targetKpiType,
    targetValue,
    showTarget: showTargetTile(tab),
  };
};

export const getTargetProgressPercent = (targetValue: number | null, forecastTotalNetSales: number) => {
  return targetValue && targetValue !== 0 ? (forecastTotalNetSales / targetValue) * 100 : 0;
};

export const buildForecastModel = (tab: SalesNetTabConfig, isToday: boolean): SalesNetForecastModel => {
  return {
    shouldRequestForecast: tab.supportsForecast && isToday,
    forecastApiName: tab.forecastApiName,
  };
};

export const normalizeNetSaleForecastToSalesNetItems = (items: Array<{ timeUnit: string; amount: number }>) => {
  return items.map((item) => ({
    timeUnit: item.timeUnit,
    netSales: item.amount,
    netSalesSCO: 0,
    netSalesRFIDSCO: 0,
    netSalesMobile: 0,
    regularNetSales: 0,
  }));
};

export const normalizeScoForecastToSalesNetItems = (items: SCOHourlyTurnoverPerformanceItem[]) => {
  return items.map((item) => ({
    timeUnit: item.timeUnit,
    netSales: 0,
    netSalesSCO: item.netSaleSCO,
    netSalesRFIDSCO: 0,
    netSalesMobile: 0,
    regularNetSales: 0,
  }));
};

export const getActiveForecastItems = (
  tab: SalesNetTabConfig,
  activeForecastApiName: SalesNetTabForecastApi,
  forecastPerformanceItems: (SalesNetPerformanceItem | null)[],
) => {
  return showForecastTile(tab) && activeForecastApiName === tab.forecastApiName ? forecastPerformanceItems : [];
};

export const getShowForecastTile = (params: {
  tab: SalesNetTabConfig;
  isForecastLoading: boolean;
  targetValue: number | null;
  activeForecastItems: (SalesNetPerformanceItem | null)[];
}) => {
  const { tab, isForecastLoading, targetValue, activeForecastItems } = params;

  return (
    showForecastTile(tab) &&
    !isForecastLoading &&
    !!targetValue &&
    activeForecastItems.some((item) => item !== null)
  );
};

export const getItemValueByTab = (item: SalesNetPerformanceItem, tab: SalesNetTabConfig) => {
  return item[tab.metricKey];
};

export const getIsNoDataByTab = (items: (SalesNetPerformanceItem | null)[], tab: SalesNetTabConfig) => {
  return getTotalNetSales(items, tab.metricKey) === 0;
};

export const buildTabLabels = (
  tabs: SalesNetTabConfig[],
  t: TFunction,
  isTabDisabledResolver: (tab: SalesNetTabConfig) => boolean = () => false,
) => {
  return tabs.map((tab) => ({
    tabName: t(tab.labelTranslation.key, tab.labelTranslation.defaultValue),
    isTabVisible: true,
    labelType: 'body3' as Typographies,
    isTabDisabled: isTabDisabledResolver(tab),
  }));
};
